/**
 * PNG Upload Validation
 *
 * Rejects a logo before it reaches the network, so an oversized or wrong-format
 * file costs nothing and the operator gets a message naming the specific limit.
 *
 * Reads the IHDR header directly rather than decoding via `new Image()`: it is
 * pure, synchronous, testable without a DOM, and it is what the backend itself
 * does. Dimensions are read before pixel data anywhere because the byte limit
 * alone does not bound memory — a 100KB PNG can declare enormous dimensions and
 * expand to gigabytes once decoded.
 *
 * Requirements: 8.7, 8.8
 */

/**
 * Maximum upload size in bytes (100 KB). The backend nominally allows 200 KB,
 * but its server rejects request bodies above ~100 KB in practice, so the
 * client enforces the limit that actually holds.
 */
export const MAX_ASSET_BYTES = 102400;

/** Maximum pixels on either axis, matching the backend. */
export const MAX_ASSET_PIXELS = 4096;

/** The eight-byte PNG signature. */
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

/** "IHDR" in ASCII. Must be the first chunk of a well-formed PNG. */
const IHDR_TYPE = [0x49, 0x48, 0x44, 0x52] as const;

/**
 * Offset of the IHDR width field: 8-byte signature + 4-byte chunk length +
 * 4-byte chunk type. Height follows immediately after.
 */
const WIDTH_OFFSET = 16;
const HEIGHT_OFFSET = 20;

/** Smallest buffer that can hold a signature and a readable IHDR width/height. */
const MIN_HEADER_BYTES = HEIGHT_OFFSET + 4;

export type PngValidation =
  | { readonly status: 'ok'; readonly width: number; readonly height: number }
  | { readonly status: 'not_png' }
  | { readonly status: 'too_large'; readonly bytes: number }
  | {
      readonly status: 'too_many_pixels';
      readonly width: number;
      readonly height: number;
    };

const matchesAt = (
  bytes: Uint8Array,
  expected: readonly number[],
  offset: number
): boolean => expected.every((byte, index) => bytes[offset + index] === byte);

/** Big-endian uint32, as every PNG integer field is encoded. */
const readUint32 = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] << 24) |
    (bytes[offset + 1] << 16) |
    (bytes[offset + 2] << 8) |
    bytes[offset + 3]) >>>
  0;

/**
 * Validate an uploaded file's bytes against the branding asset rules.
 *
 * Checks are ordered signature, then dimensions, then byte size, so the message
 * is the most useful one available: a file that is both too big and the wrong
 * shape is more usefully reported as the wrong shape.
 */
export function validatePng(bytes: Uint8Array): PngValidation {
  if (bytes.length < MIN_HEADER_BYTES) return { status: 'not_png' };
  if (!matchesAt(bytes, PNG_SIGNATURE, 0)) return { status: 'not_png' };
  if (!matchesAt(bytes, IHDR_TYPE, 12)) return { status: 'not_png' };

  const width = readUint32(bytes, WIDTH_OFFSET);
  const height = readUint32(bytes, HEIGHT_OFFSET);

  // A zero dimension is structurally invalid rather than merely unusable, so it
  // is reported as a broken file rather than as a limit the operator can adjust.
  if (width === 0 || height === 0) return { status: 'not_png' };

  if (width > MAX_ASSET_PIXELS || height > MAX_ASSET_PIXELS) {
    return { status: 'too_many_pixels', width, height };
  }

  if (bytes.length > MAX_ASSET_BYTES) {
    return { status: 'too_large', bytes: bytes.length };
  }

  return { status: 'ok', width, height };
}

/**
 * Operator-facing wording for a rejection, or `null` when the file is fine.
 *
 * Lives beside the rule so the upload control and the hook that guards the
 * request cannot describe the same rejection differently.
 */
export function describePngRejection(result: PngValidation): string | null {
  switch (result.status) {
    case 'ok':
      return null;
    case 'not_png':
      return 'That file is not a PNG. Logos must be PNG images.';
    case 'too_large':
      return `That file is ${Math.ceil(result.bytes / 1024)}KB. The maximum is ${
        MAX_ASSET_BYTES / 1024
      }KB.`;
    case 'too_many_pixels':
      return `That image is ${result.width}×${result.height}px. The maximum is ${MAX_ASSET_PIXELS}px on either axis.`;
  }
}
